import { DATA_TYPE_TO_ICON } from 'modules/queryBuilder/constants';
import { ClientDataType } from 'modules/queryBuilder/types';
import { useTranslation } from 'modules/queryBuilder/hooks/useTranslation';
import { Goal, GoalEventType } from 'modules/goals/types';
import styled from 'styled-components';
import { OverflowMenu } from 'ui/actionMenu';
import { Custom16 } from 'ui/icons/aurora/16px/Custom16';
import { Purchase16 } from 'ui/icons/aurora/16px/Purchase16';
import { BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button';
import { FlexSpacer } from 'ui/flexSpacer';
import { body1Bold, colors, spacing } from 'ui/theme';
import { Tag, TAG_SIZES } from 'ui/tags';
import { GOAL_CARD_TEST_IDS } from './GoalCard.constants';
import { useGoalGoalCardOverflowMenuOptions } from './useGoalGoalCardOverflowMenuOptions';
import { match } from 'ts-pattern';
import { useIsOverflowing } from 'utils/hooks/useIsOverflowing';
import { TruncateTooltip } from 'ui/utility/truncate/TruncateTooltip';

const GoalName = styled.div`
  width: 100%;
  display: flex;
  overflow: hidden;
  box-sizing: border-box;
  ${body1Bold}

  &&[data-truncate] {
    ${body1Bold}
  }
`;

const StyledTag = styled(Tag)`
  background-color: ${colors.backgroundSecondary};
  border-color: ${colors.grey10};
  padding: ${spacing.quarter} ${spacing.half};
  margin: 0;
`;

export const Header = ({ goal }: { goal: Goal }) => {
  const { elementRef, maxViewableWidth } = useIsOverflowing<HTMLDivElement>();

  const { t } = useTranslation();

  const overflowMenuOptions = useGoalGoalCardOverflowMenuOptions(goal);

  const event = match(goal.eventType)
    .with(GoalEventType.CustomEvent, () => (
      <>
        <Custom16 data-test={GOAL_CARD_TEST_IDS.GOAL_CUSTOM_EVENT_ICON} />
        {goal.eventName}
      </>
    ))
    .with(GoalEventType.Purchase, () => (
      <>
        <Purchase16 data-test={GOAL_CARD_TEST_IDS.GOAL_CUSTOM_EVENT_ICON} />
        {goal.eventName}
      </>
    ))
    .otherwise(() => (
      <>
        {DATA_TYPE_TO_ICON[goal.eventName as ClientDataType]}
        {t(`v2.requirementBuilder.eventName.${goal.eventName}`) || goal.eventName}
      </>
    ));

  return (
    <FlexSpacer justifyContent="space-between">
      <GoalName ref={elementRef}>
        <TruncateTooltip textOnly name={goal.name} maxWidth={maxViewableWidth} />
      </GoalName>

      <FlexSpacer gap={spacing.half}>
        <StyledTag size={TAG_SIZES.LARGE} noBoxShadow data-test={GOAL_CARD_TEST_IDS.GOAL_EVENT}>
          <FlexSpacer gap={spacing.quarter}>{event}</FlexSpacer>
        </StyledTag>
        <OverflowMenu
          data-test={GOAL_CARD_TEST_IDS.OVERFLOW}
          id={GOAL_CARD_TEST_IDS.OVERFLOW}
          options={overflowMenuOptions}
          variant={BUTTON_VARIANTS.GHOST}
          size={BUTTON_SIZES.SMALL}
        />
      </FlexSpacer>
    </FlexSpacer>
  );
};
